import { Controller } from "@hotwired/stimulus";

// Makes a chat result table something a finance user can work IN rather than only read:
// switch the number scale, sort on any column, drag a column sideways, drag a row into place,
// pin a row, filter.
//
// The card is rendered server-side by Chat::ResponseFormatter, which paints the correct figures
// on first load — this controller only re-renders when the user asks it to. Divisor table,
// precision rule and zero style all arrive as values from the same Ruby that did the first
// paint, so a scale switch cannot drift from what was originally shown.
export default class extends Controller {
  static targets = ["scaleLabel", "count", "orderReset"];
  static values = {
    scale: String,
    divisors: Object,
    unitPrecision: Number,
    commaStyle: String,
    zeroAs: String,
    decimals: { type: Number, default: -1 },   // -1 = follow the scale's default
    symbol: String,
  };

  connect() {
    // <details> is a native disclosure: the summary toggles it and NOTHING closes it. Every
    // other menu in the app shuts when you click away, so this one has to as well.
    this.dismissMenus = (event) => {
      this.element.querySelectorAll("details.laz-scale[open]").forEach((menu) => {
        if (!menu.contains(event.target)) menu.open = false;
      });
    };
    this.dismissOnEscape = (event) => {
      if (event.key !== "Escape") return;
      this.element.querySelectorAll("details.laz-scale[open]").forEach((menu) => { menu.open = false; });
    };
    document.addEventListener("click", this.dismissMenus);
    document.addEventListener("keydown", this.dismissOnEscape);

    this.wireColumnResize();
    this.sortCol = null;
    this.sortDir = null;
    this.manualOrder = null;
    this.pinned = new Set();
    this.totalRows = this.bodyRows().length;

    this.decorateRows();
    this.wireColumnDrag();
  }

  disconnect() {
    document.removeEventListener("click", this.dismissMenus);
    document.removeEventListener("keydown", this.dismissOnEscape);
  }

  // ── number formatting — mirrors Chat::ResponseFormatter#format_at_card_scale ──

  get divisor() {
    return this.divisorsValue[this.scaleValue] || 1;
  }

  // Two decimals whenever a divisor is in play: at Crores an org precision of 0 would render
  // a crore-and-a-bit as "2". An explicit choice overrides it.
  get precision() {
    if (this.decimalsValue >= 0) return this.decimalsValue;
    return this.divisor > 1 ? 2 : this.unitPrecisionValue;
  }

  setDecimals(event) {
    const asked = Number(event.currentTarget.dataset.decimals);
    if (!Number.isInteger(asked) || asked < 0 || asked > 2) return;

    this.decimalsValue = asked;
    this.repaintFigures();
    this.element.querySelectorAll("[data-decimals]").forEach((option) => {
      option.toggleAttribute("aria-current", Number(option.dataset.decimals) === asked);
    });
  }

  // Only cells the server put on the card scale carry data-raw. A count or a percentage beside
  // them has data-sort instead and is never touched.
  repaintFigures() {
    this.element.querySelectorAll("[data-raw]").forEach((cell) => {
      const text = this.format(cell.dataset.raw);
      if (text !== null) cell.textContent = text;
    });
  }

  format(raw) {
    const value = Number(raw);
    if (!Number.isFinite(value)) return null;
    if (value === 0) return this.zeroAsValue;

    const locale = this.commaStyleValue === "western" ? "en-US" : "en-IN";
    const scaled = Math.abs(value) / this.divisor;
    const text = new Intl.NumberFormat(locale, {
      minimumFractionDigits: this.precision,
      maximumFractionDigits: this.precision,
    }).format(scaled);
    return value < 0 ? `-${text}` : text;
  }

  setScale(event) {
    const scale = event.currentTarget.dataset.scale;
    if (!scale || scale === this.scaleValue) return this.closeMenu(event);

    this.scaleValue = scale;
    this.repaintFigures();

    const label = `${this.symbolValue} ${this.titleCase(scale)}`.trim();
    if (this.hasScaleLabelTarget) this.scaleLabelTarget.textContent = label;
    this.element.querySelectorAll("[data-scale]").forEach((option) => {
      option.toggleAttribute("aria-current", option.dataset.scale === scale);
    });

    const subtitle = this.element.querySelector(".card-sub");
    if (subtitle) subtitle.innerHTML = subtitle.innerHTML.replace(/amounts in .*$/, `amounts in ${label}`);

    this.closeMenu(event);
  }

  closeMenu(event) {
    const menu = event.currentTarget.closest("details");
    if (menu) menu.open = false;
  }

  titleCase(text) {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  // ── sorting ───────────────────────────────────────────────────────────────

  // Sorts on the RAW value, never the rendered text — text sorting would silently reorder the
  // table every time the scale changed.
  sortKey(row, col) {
    const cell = row.querySelector(`td[data-col="${col}"]`);
    if (!cell) return "";
    const raw = cell.dataset.raw ?? cell.dataset.sort;
    return raw === undefined ? cell.textContent.trim().toLowerCase() : Number(raw);
  }

  // Any of drag / sort / pin means the rows are no longer in the order the answer delivered
  // them, so that is exactly when a reset is worth offering.
  markReordered() {
    if (this.hasOrderResetTarget) this.orderResetTarget.hidden = false;
  }

  sort(event) {
    const header = event.currentTarget;
    const col = header.dataset.col;
    if (col === undefined) return;

    // Asking for a sort means abandoning the hand-built order — say so by doing it.
    this.manualOrder = null;
    if (this.sortCol === col) {
      this.sortDir = this.sortDir === "desc" ? "asc" : this.sortDir === "asc" ? null : "desc";
    } else {
      this.sortCol = col;
      this.sortDir = "desc";
    }
    if (!this.sortDir) this.sortCol = null;

    if (this.sortCol !== null) {
      const dir = this.sortDir === "asc" ? 1 : -1;
      const sorted = this.bodyRows().sort((a, b) => {
        const av = this.sortKey(a, col);
        const bv = this.sortKey(b, col);
        if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
        return String(av).localeCompare(String(bv)) * dir;
      });
      this.applyOrder(sorted);
    }
    this.markSortIndicators();
    this.applyPins();
    if (this.sortCol === null && !this.manualOrder && this.pinned.size === 0) {
      if (this.hasOrderResetTarget) this.orderResetTarget.hidden = true;
    } else {
      this.markReordered();
    }
  }

  markSortIndicators() {
    this.element.querySelectorAll("th.sortable").forEach((th) => {
      const active = th.dataset.col === this.sortCol && this.sortDir;
      th.dataset.dir = active ? this.sortDir : "";
      if (!active) delete th.dataset.dir;
    });
  }

  // ── filtering ─────────────────────────────────────────────────────────────

  filter(event) {
    const term = event.currentTarget.value.trim().toLowerCase();
    let shown = 0;
    this.bodyRows().forEach((row) => {
      const hit = !term || row.textContent.toLowerCase().includes(term);
      row.hidden = !hit;
      if (hit) shown += 1;
    });
    this.retotal();
    if (this.hasCountTarget) {
      this.countTarget.textContent =
        term ? `${shown} of ${this.totalRows} rows` : `${this.totalRows} row${this.totalRows === 1 ? "" : "s"}`;
    }
  }

  // A total that ignores the filter is a total of something the user is not looking at.
  retotal() {
    const foot = this.element.querySelector("tfoot tr");
    if (!foot) return;
    foot.querySelectorAll("td[data-raw]").forEach((cell) => {
      const col = cell.dataset.col;
      const sum = this.bodyRows()
        .filter((row) => !row.hidden)
        .reduce((acc, row) => {
          const value = row.querySelector(`td[data-col="${col}"]`)?.dataset.raw;
          return acc + (value === undefined ? 0 : Number(value));
        }, 0);
      cell.dataset.raw = sum;
      const text = this.format(sum);
      if (text !== null) cell.textContent = text;
    });
  }

  // ── pinning and hand-ordering rows ────────────────────────────────────────

  decorateRows() {
    // The grip is the DRAG SOURCE itself, not a button that flips `draggable` on its row at
    // mousedown. That earlier shape never started a drag: a <button> consumes the gesture, and
    // by the time mousedown fires the browser has already decided the row is not draggable.
    const grip = '<span class="rowgrip" draggable="true" role="button" tabindex="0"' +
                 ' title="Drag to move this row up or down" aria-label="Drag to move this row">\u283F</span>';
    const pin = '<button class="rowpin" title="Pin to top" aria-label="Pin to top">' +
                '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"' +
                ' stroke-linecap="round" stroke-linejoin="round">' +
                '<path d="M12 17v5"/><path d="M9 2h6l-1 7 4 3v2H6v-2l4-3z"/></svg></button>';
    this.bodyRows().forEach((row) => {
      const first = row.querySelector('td[data-col="0"]');
      if (!first) return;
      first.insertAdjacentHTML("afterbegin", grip + pin);
      this.wireRow(row, first);
    });
  }

  wireRow(row, firstCell) {
    firstCell.querySelector(".rowpin").addEventListener("click", (event) => {
      event.stopPropagation();
      const key = row.dataset.row;
      if (this.pinned.has(key)) this.pinned.delete(key);
      else this.pinned.add(key);
      this.applyPins();
      this.markReordered();
    });

    // Only the grip drags, so selecting text in a cell still works.
    const grip = firstCell.querySelector(".rowgrip");
    grip.addEventListener("click", (event) => event.stopPropagation());
    grip.addEventListener("dragstart", (event) => {
      this.dragRow = row;
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", row.dataset.row || "");
      // Drag the whole row as the ghost, not the one character in the handle.
      if (event.dataTransfer.setDragImage) event.dataTransfer.setDragImage(row, 20, 12);
      row.classList.add("row-dragging");
    });
    grip.addEventListener("dragend", () => {
      row.classList.remove("row-dragging");
      this.clearDropMarks();
      this.dragRow = null;
    });
    // Keyboard equivalent — alt+arrow moves the focused row, so the gesture is not the only way.
    grip.addEventListener("keydown", (event) => {
      if (!event.altKey || (event.key !== "ArrowUp" && event.key !== "ArrowDown")) return;
      event.preventDefault();
      const sibling = event.key === "ArrowUp" ? row.previousElementSibling : row.nextElementSibling;
      if (!sibling) return;
      row.parentNode.insertBefore(...(event.key === "ArrowUp" ? [row, sibling] : [sibling, row]));
      this.manualOrder = true;
      this.markReordered();
      grip.focus();
    });
    row.addEventListener("dragover", (event) => {
      if (!this.dragRow || this.dragRow === row) return;
      event.preventDefault();
      this.clearDropMarks();
      const box = row.getBoundingClientRect();
      row.classList.add(event.clientY < box.top + box.height / 2 ? "drop-above" : "drop-below");
    });
    row.addEventListener("drop", (event) => {
      event.preventDefault();
      if (!this.dragRow || this.dragRow === row) return;
      const box = row.getBoundingClientRect();
      const after = event.clientY >= box.top + box.height / 2;
      row.parentNode.insertBefore(this.dragRow, after ? row.nextSibling : row);
      this.clearDropMarks();
      this.manualOrder = true;
      this.markReordered();
    });
  }

  clearDropMarks() {
    this.element.querySelectorAll(".drop-above, .drop-below").forEach((row) => {
      row.classList.remove("drop-above", "drop-below");
    });
  }

  applyPins() {
    const body = this.element.querySelector("tbody");
    if (!body) return;
    // Pins float ABOVE the sort rather than replacing it, so two rows forty apart can sit
    // side by side while everything else stays sorted.
    this.bodyRows()
      .filter((row) => this.pinned.has(row.dataset.row))
      .reverse()
      .forEach((row) => body.insertBefore(row, body.firstChild));
    this.bodyRows().forEach((row) => {
      const on = this.pinned.has(row.dataset.row);
      row.classList.toggle("pinned", on);
      const pin = row.querySelector(".rowpin");
      if (pin) {
        pin.classList.toggle("on", on);
        pin.title = on ? "Unpin" : "Pin to top";
      }
    });
  }

  // Back to the order the answer arrived in — hand-drags, sort AND pins, because all three
  // moved the rows and a half-reset is worse than none.
  resetOrder() {
    this.manualOrder = null;
    this.sortCol = null;
    this.sortDir = null;
    this.pinned.clear();
    this.applyOrder(this.bodyRows().sort((a, b) => Number(a.dataset.seq) - Number(b.dataset.seq)));
    this.markSortIndicators();
    this.applyPins();
    if (this.hasOrderResetTarget) this.orderResetTarget.hidden = true;
  }

  // ── column drag ───────────────────────────────────────────────────────────

  wireColumnDrag() {
    this.bodyRows().forEach((row, i) => { row.dataset.seq = i; });

    this.element.querySelectorAll('th[draggable="true"]').forEach((th) => {
      th.addEventListener("dragstart", (event) => {
        this.dragCol = th.dataset.col;
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", this.dragCol);
        th.classList.add("col-dragging");
      });
      th.addEventListener("dragend", () => {
        th.classList.remove("col-dragging");
        this.element.querySelectorAll(".col-drop").forEach((x) => x.classList.remove("col-drop"));
      });
      th.addEventListener("dragover", (event) => {
        if (!this.dragCol || this.dragCol === th.dataset.col) return;
        event.preventDefault();
        th.classList.add("col-drop");
      });
      th.addEventListener("dragleave", () => th.classList.remove("col-drop"));
      th.addEventListener("drop", (event) => {
        event.preventDefault();
        th.classList.remove("col-drop");
        if (!this.dragCol || this.dragCol === th.dataset.col) return;
        this.moveColumn(this.dragCol, th.dataset.col);
        this.dragCol = null;
      });
    });
  }

  moveColumn(from, to) {
    this.element.querySelectorAll("tr").forEach((row) => {
      const source = row.querySelector(`[data-col="${from}"]`);
      const target = row.querySelector(`[data-col="${to}"]`);
      if (!source || !target) return;
      const movingRight = [...row.children].indexOf(source) < [...row.children].indexOf(target);
      row.insertBefore(source, movingRight ? target.nextSibling : target);
    });
  }

  // ── dimension column widths ───────────────────────────────────────────────
  // A party name or a description is the cell that cannot be abbreviated without the row losing
  // its meaning, and every dimension column is clamped to keep one long value from pushing the
  // figures off screen. Wrapping was the obvious alternative and the wrong one: it spends
  // vertical space on EVERY row to rescue a few long ones, which is what made the old table
  // unreadable. So any of them can be widened instead — drag its edge, or double-click to fit
  // its longest value exactly. Numeric columns are not resizable: they are already exactly as
  // wide as their content, and narrowing one would clip a figure.
  wireColumnResize() {
    this.element.querySelectorAll(".laz-grid th:not(.num)").forEach((th) => {
      const col = th.dataset.col;
      if (col === undefined) return;

      const handle = document.createElement("span");
      handle.className = "laz-colresize";
      handle.title = "Drag to widen · double-click to fit the longest value";
      th.appendChild(handle);

      let startX = 0;
      let startWidth = 0;

      const onMove = (event) => {
        // 90px floor so a column cannot be dragged away entirely.
        this.setColumnWidth(col, Math.max(90, startWidth + event.clientX - startX));
      };
      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        document.body.classList.remove("laz-resizing");
      };

      handle.addEventListener("mousedown", (event) => {
        event.preventDefault();
        event.stopPropagation();        // a drag on the edge is not a click on the header
        startX = event.clientX;
        startWidth = th.getBoundingClientRect().width;
        document.body.classList.add("laz-resizing");
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
      });

      handle.addEventListener("click", (event) => event.stopPropagation());
      handle.addEventListener("dblclick", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.fitColumnWidth(th, col);
      });
    });
  }

  setColumnWidth(col, px) {
    const width = `${Math.round(px)}px`;
    this.element.querySelectorAll(`[data-col="${col}"]`).forEach((cell) => {
      cell.style.width = width;
      cell.style.maxWidth = width;
    });
  }

  // Measure the text rather than letting the browser reflow the table: the full value lives in
  // each cell's title attribute, so nothing has to be un-truncated to read it.
  fitColumnWidth(th, col) {
    const probe = document.createElement("canvas").getContext("2d");
    const style = window.getComputedStyle(th);
    probe.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;

    let widest = probe.measureText(th.textContent.trim()).width;
    this.bodyRows().forEach((row) => {
      const cell = row.querySelector(`td[data-col="${col}"]`);
      if (!cell) return;
      const text = cell.getAttribute("title") || cell.textContent;
      widest = Math.max(widest, probe.measureText(text.trim()).width);
    });

    // padding, plus the grip and pin on the first column, plus a little air — and a ceiling so
    // one absurd value cannot push every figure off the screen.
    const chrome = col === "0" ? 78 : 40;
    this.setColumnWidth(col, Math.min(widest + chrome, 560));
  }

  // ── helpers ───────────────────────────────────────────────────────────────

  bodyRows() {
    return [...this.element.querySelectorAll("tbody tr")];
  }

  applyOrder(rows) {
    const body = this.element.querySelector("tbody");
    if (body) rows.forEach((row) => body.appendChild(row));
  }
}
